import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import NotificationBell from './NotificationBell';
import LanguageSwitcher from './LanguageSwitcher';

interface SidebarProps {
    /** Mobile drawer open state. Ignored at md+, where the sidebar is always visible. */
    isOpen?: boolean;
    onClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen = false, onClose }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const menuItems = [
        {
            path: '/',
            icon: 'fa-users-gear',
            label: t('sidebar.users'),
        },
        {
            path: '/calendars',
            icon: 'fa-calendar',
            label: t('sidebar.calendars'),
        },
        {
            path: '/training-library',
            icon: 'fa-film',
            label: t('sidebar.trainingLibrary'),
        },
    ];

    const isActive = (path: string) => {
        if (path === '/') {
            return location.pathname === '/' || location.pathname === '/users';
        }
        return location.pathname === path;
    };

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
    };

    return (
        <aside
            className={`w-72 sidebar-pattern border-r border-slate-700 flex flex-col text-slate-300 fixed inset-y-0 left-0 z-[60] transition-transform duration-200 ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:static md:flex-shrink-0 md:z-auto md:translate-x-0 md:transition-none`}
        >
            {/* Logo */}
            <div className="h-24 flex items-center px-5 border-b border-slate-700 bg-slate-900/50">
                <div className="flex justify-start w-full">
                    <img src="/logo.png" alt="Enjoy Triathlon" className="w-44 h-auto object-contain" />
                </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 overflow-y-auto py-8 px-4 space-y-2">
                {menuItems.map((item) => (
                    <button
                        key={item.path}
                        onClick={() => { navigate(item.path); onClose?.(); }}
                        className={`w-full flex items-center gap-4 px-4 py-3 rounded-xl transition-all ${isActive(item.path)
                                ? 'bg-gradient-to-r from-primary/20 to-transparent border-l-4 border-primary text-white'
                                : 'hover:bg-slate-800/50 text-slate-400 hover:text-white border-l-4 border-transparent'
                            }`}
                    >
                        <div className="w-8 flex justify-center relative z-10">
                            <i className={`fa-solid ${item.icon} text-lg ${isActive(item.path) ? 'text-primary' : ''}`}></i>
                        </div>
                        <span className="font-semibold relative z-10">{item.label}</span>
                    </button>
                ))}
            </nav>

            {/* Notifications */}
            <NotificationBell />

            {/* Language Switcher */}
            <div className="px-4">
                <LanguageSwitcher />
            </div>

            {/* Logout */}
            <div className="px-4 pb-4">
                <button
                    onClick={handleLogout}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-red-400 hover:text-red-300 rounded-lg transition-colors font-medium text-sm"
                >
                    <i className="fa-solid fa-right-from-bracket"></i>
                    <span>{t('sidebar.logout')}</span>
                </button>
            </div>
        </aside>
    );
};

export default Sidebar;
